import type { PublicRecord, VisibilityType } from '@/types/publicRecords';
import { hasProofSummary, isCertificateIssued, isCommunitySummaryVisible } from '@/lib/recordTrust';

/** Only these visibilities may appear on the signed-in Geospatial Map. */
export const GEOSPATIAL_ALLOWED_VISIBILITIES: VisibilityType[] = [
  'community_summary',
  'institution_public',
  'verified_public_proof',
];

export type GeospatialMarkerKind = 'individual' | 'institution' | 'heritage' | 'proof_card';

export type GeospatialMapMode = 'map' | 'satellite' | 'terrain' | 'density';

/** Primary geospatial explorer surfaces */
export type GeospatialViewMode = 'atlas' | 'hologram' | GeospatialMapMode;

export function isTacticalViewMode(mode: GeospatialViewMode): mode is GeospatialMapMode {
  return mode === 'map' || mode === 'satellite' || mode === 'terrain' || mode === 'density';
}

export function viewModeLabel(mode: GeospatialViewMode): string {
  switch (mode) {
    case 'atlas':
      return 'Atlas View';
    case 'hologram':
      return 'Vault Insights';
    case 'satellite':
      return 'Satellite';
    case 'terrain':
      return '3D Terrain';
    case 'density':
      return 'Archive Density';
    default:
      return 'Local Geospatial Map';
  }
}

export type GeospatialRecordTypeFilter =
  | 'all'
  | 'individual_record'
  | 'institution_record'
  | 'heritage_record'
  | 'proof_card';

export type GeospatialCluster = {
  id: string;
  lngLat: [number, number];
  count: number;
  records: PublicRecord[];
  name: string;
  dominantKind: GeospatialMarkerKind;
};

export type GeospatialPlaceTypeCounts = {
  individual: number;
  institution: number;
  heritage: number;
  proof_card: number;
};

/** Minimum quality bar for community-public map display (not a raw submission dump). */
export function passesGeospatialPublicQuality(record: PublicRecord): boolean {
  if (record.isArchived) return false;
  if (record.verificationStatus === 'Draft' || record.verificationStatus === 'Submitted') {
    return false;
  }
  if (!record.title?.trim() || record.title.trim().length < 4) return false;
  if (!record.description?.trim() || record.description.trim().length < 20) return false;
  if (!record.locationName?.trim()) return false;
  if (record.evidenceCount < 1 && !hasProofSummary(record)) return false;
  return true;
}

/** Certificates are never placed on the map directly — only proof-safe summaries. */
export function isGeospatialCertificateOnly(record: PublicRecord): boolean {
  return (
    isCertificateIssued(record) &&
    !hasProofSummary(record) &&
    (record.recordType === 'institution_certificate' || record.recordType === 'public_proof_card')
  );
}

export function isGeospatialVisible(record: PublicRecord): boolean {
  if (record.latitude == null || record.longitude == null) return false;
  if (record.visibility === 'private' || record.visibility === 'external_link') return false;
  if (!GEOSPATIAL_ALLOWED_VISIBILITIES.includes(record.visibility)) return false;
  if (record.showOnGeospatialMap !== true) return false;
  if (record.approvedForPublicMap !== true) return false;
  if (!passesGeospatialPublicQuality(record)) return false;
  if (isGeospatialCertificateOnly(record)) return false;
  return true;
}

/** Default map eligibility when explicit approval flag is omitted on demo records. */
export function isGeospatialMapEligible(record: PublicRecord): boolean {
  if (!GEOSPATIAL_ALLOWED_VISIBILITIES.includes(record.visibility)) return false;
  if (record.showOnGeospatialMap !== true) return false;
  if (record.approvedForPublicMap !== true) return false;
  if (!passesGeospatialPublicQuality(record)) return false;
  if (isGeospatialCertificateOnly(record)) return false;
  return record.latitude != null && record.longitude != null && record.visibility !== 'private';
}

export function getGeospatialMarkerKind(record: PublicRecord): GeospatialMarkerKind {
  if (record.recordType === 'heritage_archive' || record.recordType === 'cultural_collection') {
    return 'heritage';
  }
  if (hasProofSummary(record) && isCommunitySummaryVisible(record)) return 'proof_card';
  if (record.ownerType === 'institution') return 'institution';
  return 'individual';
}

export function matchesGeospatialRecordType(
  record: PublicRecord,
  filter: GeospatialRecordTypeFilter
): boolean {
  if (filter === 'all') return true;
  const kind = getGeospatialMarkerKind(record);
  if (filter === 'individual_record') return kind === 'individual';
  if (filter === 'institution_record') return kind === 'institution';
  if (filter === 'heritage_record') return kind === 'heritage';
  if (filter === 'proof_card') return kind === 'proof_card';
  return true;
}

export function countPlaceTypes(records: PublicRecord[]): GeospatialPlaceTypeCounts {
  const counts: GeospatialPlaceTypeCounts = {
    individual: 0,
    institution: 0,
    heritage: 0,
    proof_card: 0,
  };
  for (const r of records) {
    counts[getGeospatialMarkerKind(r)] += 1;
  }
  return counts;
}

export const GEOSPATIAL_MARKER_STYLES: Record<
  GeospatialMarkerKind,
  { color: string; bg: string; border: string; label: string }
> = {
  individual: {
    color: '#22d3ee',
    bg: 'rgba(34,211,238,0.18)',
    border: 'rgba(34,211,238,0.55)',
    label: 'Individual Record',
  },
  institution: {
    color: '#a78bfa',
    bg: 'rgba(167,139,250,0.18)',
    border: 'rgba(167,139,250,0.55)',
    label: 'Institution Record',
  },
  heritage: {
    color: '#e9a84c',
    bg: 'rgba(233,168,76,0.18)',
    border: 'rgba(233,168,76,0.55)',
    label: 'Heritage Record',
  },
  proof_card: {
    color: '#34d399',
    bg: 'rgba(52,211,153,0.18)',
    border: 'rgba(52,211,153,0.55)',
    label: 'Proof Card',
  },
};

export function clusterSizePx(count: number): number {
  if (count >= 50) return 46;
  if (count >= 10) return 38;
  return 30;
}

export function clusterSizeColor(count: number): string {
  if (count >= 50) return '#e9a84c';
  if (count >= 10) return '#a78bfa';
  return '#22d3ee';
}

export function mapModeDisplayLabel(mode: GeospatialMapMode): string {
  switch (mode) {
    case 'satellite':
      return 'Satellite';
    case 'terrain':
      return '3D Terrain';
    case 'density':
      return 'Archive Density';
    default:
      return 'OSM';
  }
}

function clusterKey(record: PublicRecord, precision: number): string {
  const name = (record.locationName || '').trim().toLowerCase();
  if (name.length > 3) return `name:${name}`;
  return `geo:${record.longitude!.toFixed(precision)},${record.latitude!.toFixed(precision)}`;
}

/** Group nearby public records into map clusters. */
export function buildGeospatialClusters(
  records: PublicRecord[],
  precision = 2
): GeospatialCluster[] {
  const groups = new Map<string, PublicRecord[]>();
  for (const record of records) {
    const key = clusterKey(record, precision);
    const list = groups.get(key) ?? [];
    list.push(record);
    groups.set(key, list);
  }

  return [...groups.entries()].map(([key, clusterRecords]) => {
    const lng = clusterRecords.reduce((s, r) => s + r.longitude!, 0) / clusterRecords.length;
    const lat = clusterRecords.reduce((s, r) => s + r.latitude!, 0) / clusterRecords.length;
    const name =
      clusterRecords[0].locationName?.trim() ||
      `${lat.toFixed(2)}°N · ${Math.abs(lng).toFixed(2)}°W`;
    const kinds = clusterRecords.map(getGeospatialMarkerKind);
    const dominantKind = kinds.includes('heritage')
      ? 'heritage'
      : kinds.includes('proof_card')
        ? 'proof_card'
        : kinds.includes('institution')
          ? 'institution'
          : 'individual';

    return {
      id: key,
      lngLat: [lng, lat] as [number, number],
      count: clusterRecords.length,
      records: clusterRecords,
      name,
      dominantKind,
    };
  });
}

export function geospatialRecordId(record: PublicRecord): string {
  return record.proofCardId ?? record.id;
}

export type GeospatialRegionFilter = 'all' | 'vancouver' | 'bc' | 'coast';

export type GeospatialDateFilter = 'all' | '90d' | '1y';

export function matchesGeospatialRegion(
  record: PublicRecord,
  region: GeospatialRegionFilter
): boolean {
  if (region === 'all') return true;
  const loc = record.locationName.toLowerCase();
  if (region === 'vancouver') {
    return loc.includes('vancouver') || loc.includes('convention') || loc.includes('granville');
  }
  if (region === 'coast') {
    return loc.includes('coast') || loc.includes('sunshine') || loc.includes('island');
  }
  return loc.includes('british columbia') || loc.includes('bc') || loc.includes('vancouver');
}

export function matchesGeospatialDateRange(
  record: PublicRecord,
  range: GeospatialDateFilter
): boolean {
  if (range === 'all') return true;
  const ts = new Date(record.date).getTime();
  const now = Date.now();
  const days = range === '90d' ? 90 : 365;
  return ts >= now - days * 86_400_000;
}
